import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Sparkles, Truck, Tag, Ticket } from "lucide-react";

const ICONS = { sparkles: Sparkles, truck: Truck, tag: Tag, ticket: Ticket };

const OfferTicker = ({ config }) => {
  const { enabled = true, autoplayMs = 4500, offers = [] } = config || {};
  const [idx, setIdx] = useState(0);
  const paused = useRef(false);

  useEffect(() => {
    if (!enabled || offers.length <= 1) return;
    const id = setInterval(() => {
      if (!paused.current) setIdx((i) => (i + 1) % offers.length);
    }, autoplayMs);
    return () => clearInterval(id);
  }, [enabled, autoplayMs, offers.length]);

  if (!enabled || offers.length === 0) return null;

  const go = (dir) => setIdx((i) => (i + dir + offers.length) % offers.length);

  return (
    <section
      data-testid="offer-ticker"
      className="w-full bg-[color:var(--vw-bg)]"
      onMouseEnter={() => { paused.current = true; }}
      onMouseLeave={() => { paused.current = false; }}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 h-[92px] flex items-center gap-4">
        <button
          data-testid="offer-prev"
          aria-label="Previous offer"
          onClick={() => go(-1)}
          className="w-9 h-9 flex items-center justify-center text-[color:var(--vw-ink-soft)] hover:text-[color:var(--vw-ink)] transition-colors"
        >
          <ChevronLeft size={18} strokeWidth={1.5} />
        </button>

        {/* Desktop: show all 4 with thin separators; highlight the active one */}
        <ul className="hidden md:flex flex-1 items-center justify-between">
          {offers.map((o, i) => {
            const Icon = ICONS[o.icon] || Sparkles;
            const active = i === idx;
            return (
              <li
                key={i}
                data-testid={`offer-item-${i}`}
                className={`flex-1 flex items-center justify-center gap-3 px-4 transition-opacity duration-500 ${
                  active ? "opacity-100" : "opacity-55"
                } ${i > 0 ? "border-l border-[color:var(--vw-beige-dark)]/70" : ""}`}
              >
                <Icon size={16} strokeWidth={1.5} className="text-[color:var(--vw-ink)]" />
                <span className="overline text-[color:var(--vw-ink)] whitespace-nowrap">{o.text}</span>
              </li>
            );
          })}
        </ul>

        {/* Mobile: single-slide crossfade */}
        <div className="md:hidden flex-1 relative h-full flex items-center justify-center overflow-hidden">
          {offers.map((o, i) => {
            const Icon = ICONS[o.icon] || Sparkles;
            const active = i === idx;
            return (
              <div
                key={i}
                data-testid={`offer-slide-${i}`}
                className={`absolute inset-0 flex items-center justify-center gap-3 transition-opacity duration-500 ${
                  active ? "opacity-100" : "opacity-0 pointer-events-none"
                }`}
              >
                <Icon size={16} strokeWidth={1.5} />
                <span className="overline text-[color:var(--vw-ink)] whitespace-nowrap">{o.text}</span>
              </div>
            );
          })}
        </div>

        <button
          data-testid="offer-next"
          aria-label="Next offer"
          onClick={() => go(1)}
          className="w-9 h-9 flex items-center justify-center text-[color:var(--vw-ink-soft)] hover:text-[color:var(--vw-ink)] transition-colors"
        >
          <ChevronRight size={18} strokeWidth={1.5} />
        </button>
      </div>
    </section>
  );
};

export default OfferTicker;
